/**
 * If this asset is included twice, a second top-level parse would reuse the same
 * global lexical scope and can throw on redeclared const after minify.
 * Guard + block scope avoids that without an IIFE.
 */
if (!window.__pmWishlistAuthGuardLoaded) {
  window.__pmWishlistAuthGuardLoaded = true;

const SELECTORS = {
  wishlistButton: '[data-swaction="addToWishlist"], .swym-add-to-wishlist-view-product',
  variantInput: 'form [name="id"]',
};

const QUERY_PARAMS = {
  returnTo: 'return_to',
  wishlistVariantId: 'wishlist_variant_id',
  wishlistProductId: 'wishlist_product_id',
  wishlistProductUrl: 'wishlist_product_url',
};

const MODAL_SELECTOR = 'pm-wishlist-auth-modal';
const PENDING_WISHLIST_SESSION_KEY = 'pmWishlistPendingAddProcessed';
const PENDING_BUTTON_UI_KEY = 'pmWishlistPendingButtonUi';
const WISHLIST_SUCCESS_CLASS = 'swym-added';
const INIT_RETRY_TIMEOUT_MS = 1200;
const DIRECT_BOUND_ATTR = 'data-pm-wishlist-direct-bound';
const SEARCH_RESULTS_UPDATED_EVENT = 'pm:wishlist-search-results-updated';
let listenersAttached = false;
let swymAddedUiListenerAttached = false;
/** After post-login auto-add: re-apply .swym-added when new buttons mount (e.g. search). */
let activeWishlistUiTarget = null;
let activeWishlistUiTargetClearId = null;

const getLoginUrl = () => {
  const loginUrl = window.pmWishlistAuth?.loginUrl;
  if (typeof loginUrl === 'string' && loginUrl.trim()) return loginUrl;
  return '/account/login';
};

const isLoggedIn = () => window.pmWishlistAuth?.isLoggedIn === true;

const logHandledButton = (button, source) => {
  if (!button) return;
  const details = {
    source,
    product_id: button.dataset?.productId || null,
    variant_id: button.dataset?.variantId || null,
    product_url: button.dataset?.productUrl || null,
    classes: button.className || null,
  };
};

const getWishlistButtonFromEvent = (event) => {
  if (!event) return null;

  if (typeof event.composedPath === 'function') {
    const path = event.composedPath();
    for (const node of path) {
      if (node instanceof Element && node.matches(SELECTORS.wishlistButton)) {
        return node;
      }
    }
  }

  if (event.target instanceof Element) {
    return event.target.closest(SELECTORS.wishlistButton);
  }

  if (event.target?.parentElement instanceof Element) {
    return event.target.parentElement.closest(SELECTORS.wishlistButton);
  }

  return null;
};

const getVariantIdFromButton = (button) => {
  const variantInput = button?.closest('form')?.querySelector(SELECTORS.variantInput);
  if (variantInput?.value) return String(variantInput.value).trim();
  return button?.dataset?.variantId?.trim() ?? '';
};

const extractHandleFromUrl = (productUrl) => {
  if (!productUrl) return '';
  try {
    const parsed = new URL(productUrl, window.location.origin);
    const parts = parsed.pathname.split('/').filter(Boolean);
    return parts[0] === 'products' ? parts[1] ?? '' : '';
  } catch {
    return '';
  }
};

const getProductMetaFromButton = (button) => {
  const productId = button?.dataset?.productId?.trim() ?? '';
  const productHandle = button?.dataset?.productHandle?.trim()
    || extractHandleFromUrl(button?.dataset?.productUrl?.trim() ?? '');
  const productUrl = button?.dataset?.productUrl?.trim() ?? '';

  return { productId, productHandle, productUrl };
};

const buildReturnToUrl = ({ variantId, productId, productUrl }) => {
  const returnToUrl = new URL(window.location.href);
  if (variantId) {
    returnToUrl.searchParams.set(QUERY_PARAMS.wishlistVariantId, variantId);
  }
  if (productId) {
    returnToUrl.searchParams.set(QUERY_PARAMS.wishlistProductId, productId);
  }
  if (productUrl) {
    returnToUrl.searchParams.set(QUERY_PARAMS.wishlistProductUrl, productUrl);
  }
  return `${returnToUrl.pathname}${returnToUrl.search}${returnToUrl.hash}`;
};

const redirectToLogin = ({ variantId, productId, productUrl }) => {
  try {
    if (productId && variantId) {
      sessionStorage.setItem(
        PENDING_BUTTON_UI_KEY,
        JSON.stringify({ productId: String(productId), variantId: String(variantId) })
      );
    }
  } catch {
    console.error('Failed to set pending button UI key');
  }

  const loginUrl = new URL(getLoginUrl(), window.location.origin);
  const returnTo = buildReturnToUrl({ variantId, productId, productUrl });
  loginUrl.searchParams.set(QUERY_PARAMS.returnTo, returnTo);
  loginUrl.searchParams.set('return_url', returnTo);

  const redirectPath = `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`;
  window.location.assign(redirectPath);
};

const markWishlistButtonsAddedState = (productId, variantId) => {
  if (!productId || !variantId) return;
  const pid = String(productId);
  const vid = String(variantId);
  document.querySelectorAll(SELECTORS.wishlistButton).forEach((btn) => {
    if (btn.dataset?.productId === pid && btn.dataset?.variantId === vid) {
      btn.classList.add(WISHLIST_SUCCESS_CLASS);
      btn.setAttribute('aria-pressed', 'true');
    }
  });
};

/** Re-run when wishlist buttons mount later (search dropdown, lazy sections). */
const markWishlistButtonsAddedStateDeferred = (productId, variantId) => {
  activeWishlistUiTarget = { productId: String(productId), variantId: String(variantId) };
  if (activeWishlistUiTargetClearId) clearTimeout(activeWishlistUiTargetClearId);
  activeWishlistUiTargetClearId = setTimeout(() => {
    activeWishlistUiTarget = null;
    activeWishlistUiTargetClearId = null;
  }, 12000);

  markWishlistButtonsAddedState(productId, variantId);
  requestAnimationFrame(() => markWishlistButtonsAddedState(productId, variantId));
  setTimeout(() => markWishlistButtonsAddedState(productId, variantId), 300);
  setTimeout(() => markWishlistButtonsAddedState(productId, variantId), 1500);
};

const clearPendingButtonUiKey = () => {
  try {
    sessionStorage.removeItem(PENDING_BUTTON_UI_KEY);
  } catch {
    /* ignore */
  }
};

const attachSwymAddedToWishlistUiListener = () => {
  if (swymAddedUiListenerAttached) return;
  swymAddedUiListenerAttached = true;
  withSwat((swat) => {
    if (!swat.evtLayer || !swat.JSEvents?.addedToWishlist) return;
    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
      const d = event?.detail?.d;
      if (d?.empi != null && d?.epi != null) {
        markWishlistButtonsAddedStateDeferred(String(d.empi), String(d.epi));
      }
    });
  });
};

if (!customElements.get('pm-wishlist-auth-modal')) {
  customElements.define('pm-wishlist-auth-modal', class extends HTMLElement {
    constructor() {
      super();
      this.handleEscape = this.handleEscape.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.pendingWishlistPayload = null;
      this.overlay = this.querySelector('[data-overlay]');
      this._closeFallbackTimer = null;
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleEscape);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleEscape);
    }

    open(payload) {
      if (this._closeFallbackTimer) {
        clearTimeout(this._closeFallbackTimer);
        this._closeFallbackTimer = null;
      }
      this.pendingWishlistPayload = payload ?? null;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-open');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('is-open'));
      });
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      if (this._closeFallbackTimer) clearTimeout(this._closeFallbackTimer);

      const finish = () => {
        this._closeFallbackTimer = null;
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
      };

      const overlay = this.overlay;
      if (!overlay) {
        finish();
        return;
      }

      const onEnd = (event) => {
        if (event.target !== overlay) return;
        if (event.propertyName !== 'opacity') return;
        overlay.removeEventListener('transitionend', onEnd);
        finish();
      };

      overlay.addEventListener('transitionend', onEnd);
      this._closeFallbackTimer = setTimeout(() => {
        overlay.removeEventListener('transitionend', onEnd);
        finish();
      }, 400);
    }

    handleEscape(event) {
      if (event.key === 'Escape' && !this.hidden) this.close();
    }

    handleClick(event) {
      const actionButton = event.target instanceof Element ? event.target.closest('[data-action]') : null;
      if (actionButton) {
        if (actionButton.getAttribute('data-action') === 'login') {
          if (this.pendingWishlistPayload) {
            redirectToLogin(this.pendingWishlistPayload);
          }
          return;
        }
        this.close();
        return;
      }

      if (event.target === this.overlay) this.close();
    }
  });
}

const removeWishlistParamsFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(QUERY_PARAMS.wishlistVariantId);
  url.searchParams.delete(QUERY_PARAMS.wishlistProductId);
  url.searchParams.delete(QUERY_PARAMS.wishlistProductUrl);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

const parseWishlistPayloadFromUrl = () => {
  const url = new URL(window.location.href);
  const variantId = url.searchParams.get(QUERY_PARAMS.wishlistVariantId) ?? '';
  const productId = url.searchParams.get(QUERY_PARAMS.wishlistProductId) ?? '';
  const productUrl = url.searchParams.get(QUERY_PARAMS.wishlistProductUrl) ?? '';

  if (!variantId || !productId || !productUrl) return null;

  const parsedVariantId = Number(variantId);
  const parsedProductId = Number(productId);
  if (!Number.isFinite(parsedVariantId) || !Number.isFinite(parsedProductId)) return null;

  return {
    epi: parsedVariantId,
    empi: parsedProductId,
    du: productUrl,
  };
};

const buildPendingWishlistSessionValue = (payload) => {
  return `${payload.epi}:${payload.empi}:${payload.du}`;
};

const withSwat = (callback) => {
  if (window.swat) {
    callback(window.swat);
    return;
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => callback(swat));
};

const addPendingWishlistItemFromUrl = () => {
  if (!isLoggedIn()) return;

  const payload = parseWishlistPayloadFromUrl();
  if (!payload) return;
  const payloadSessionValue = buildPendingWishlistSessionValue(payload);
  if (sessionStorage.getItem(PENDING_WISHLIST_SESSION_KEY) === payloadSessionValue) {
    removeWishlistParamsFromUrl();
    markWishlistButtonsAddedStateDeferred(payload.empi, payload.epi);
    clearPendingButtonUiKey();
    return;
  }

  // Remove params first to prevent repeated auto-add attempts on reload/error.
  removeWishlistParamsFromUrl();

  withSwat((swat) => {
    swat.fetch(
      (products) => {
        const alreadyInWishlist = products.some(
          (product) => Number(product?.epi) === payload.epi
        );

        if (alreadyInWishlist) {
          sessionStorage.setItem(PENDING_WISHLIST_SESSION_KEY, payloadSessionValue);
          markWishlistButtonsAddedStateDeferred(payload.empi, payload.epi);
          clearPendingButtonUiKey();
          return;
        }

        swat.addToWishList(
          payload,
          (res) => {
            sessionStorage.setItem(PENDING_WISHLIST_SESSION_KEY, payloadSessionValue);
            markWishlistButtonsAddedStateDeferred(payload.empi, payload.epi);
            clearPendingButtonUiKey();
          },
          (err) => {
            console.log('Wishlist add error', err);
          }
        );

      },
      (error) => console.log('Wishlist fetch error', error)
    );
  });
};

const handleGuestWishlistClick = (button, event = null) => {
  if (isLoggedIn()) return true;
  if (!button) return true;

  logHandledButton(button, event?.type || 'manual');

  if (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  const variantId = getVariantIdFromButton(button);
  const { productId, productHandle, productUrl } = getProductMetaFromButton(button);

  const modal = document.querySelector(MODAL_SELECTOR);
  if (!modal || typeof modal.open !== 'function') return false;
  modal.open({
    variantId,
    productId,
    productUrl,
  });

  return false;
};

const onWishlistClickCapture = (event) => {
  if (isLoggedIn()) return;

  const button = getWishlistButtonFromEvent(event);
  if (!button) return;
  handleGuestWishlistClick(button, event);
};

const onDirectWishlistInteraction = (event) => {
  if (isLoggedIn()) return;
  const button = event.currentTarget;
  if (!(button instanceof Element)) return;
  handleGuestWishlistClick(button, event);
};

const bindWishlistButtonDirect = (button) => {
  if (!(button instanceof Element)) return false;
  if (button.hasAttribute(DIRECT_BOUND_ATTR)) return false;
  button.setAttribute(DIRECT_BOUND_ATTR, 'true');
  button.addEventListener('pointerdown', onDirectWishlistInteraction, true);
  button.addEventListener('click', onDirectWishlistInteraction, true);
  return true;
};

const scanRootForWishlistButtons = (root) => {
  if (!root || root.nodeType !== Node.ELEMENT_NODE) return 0;
  let added = 0;
  if (root.matches(SELECTORS.wishlistButton)) {
    if (bindWishlistButtonDirect(root)) added += 1;
  }
  root.querySelectorAll(SELECTORS.wishlistButton).forEach((btn) => {
    if (bindWishlistButtonDirect(btn)) added += 1;
  });
  return added;
};

const scanDocumentWishlistButtons = () => {
  const root = document.documentElement;
  if (!root) return 0;
  return scanRootForWishlistButtons(root);
};

const onSearchResultsUpdated = (event) => {
  const count = scanDocumentWishlistButtons();
  if (activeWishlistUiTarget) {
    markWishlistButtonsAddedState(activeWishlistUiTarget.productId, activeWishlistUiTarget.variantId);
  }
};

const init = () => {
  window.pmWishlistAuth = window.pmWishlistAuth || {};
  window.pmWishlistAuth.handleGuestWishlistClick = (event, button) => {
    const targetButton = button instanceof Element ? button : getWishlistButtonFromEvent(event);
    return handleGuestWishlistClick(targetButton, event);
  };
  window.pmWishlistAuth.markWishlistButtonAdded = (productId, variantId) => {
    markWishlistButtonsAddedStateDeferred(productId, variantId);
  };
  window.pmWishlistAuth.debug = () => ({
    isLoggedIn: isLoggedIn(),
    buttons: document.querySelectorAll(SELECTORS.wishlistButton).length,
    modalFound: !!document.querySelector(MODAL_SELECTOR),
  });

  addPendingWishlistItemFromUrl();
  scanDocumentWishlistButtons();

  if (!listenersAttached) {
    // Attach on window (capture) so we run before third-party document listeners.
    window.addEventListener('pointerdown', onWishlistClickCapture, true);
    window.addEventListener('click', onWishlistClickCapture, true);
    document.documentElement.addEventListener('pointerdown', onWishlistClickCapture, true);
    document.documentElement.addEventListener('click', onWishlistClickCapture, true);
    document.addEventListener(SEARCH_RESULTS_UPDATED_EVENT, onSearchResultsUpdated);
    attachSwymAddedToWishlistUiListener();
    listenersAttached = true;
  }

  const trackedButtons = document.querySelectorAll(SELECTORS.wishlistButton);
  trackedButtons.forEach((button, index) => {
    button.dataset.pmWishlistTracked = 'true';
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    try {
      init();
      setTimeout(init, INIT_RETRY_TIMEOUT_MS);
    } catch (error) {
      console.error('[Wishlist Auth Guard] Init error:', error);
    }
  });
} else {
  try {
    init();
    setTimeout(init, INIT_RETRY_TIMEOUT_MS);
  } catch (error) {
    console.error('[Wishlist Auth Guard] Init error:', error);
  }
}
}
